import { FC, Fragment, useEffect, useState } from "react";
import { Checkbox, Dialog, DialogPanel, Transition } from "@headlessui/react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeCardElementOptions } from "@stripe/stripe-js";
import Input from "shared/Input/Input";
import ButtonCircle from "shared/Button/ButtonCircle";
import { useNavigate, useNavigationType } from "react-router-dom";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { globalJson } from "global/global_json";
import { useData } from "data/data-provider";
import { encodeBase64 } from "@progress/kendo-file-saver";
import { getRentalAgreementContent } from "api/rental-agreement/rental-agreement";
import { PaymentDone } from "api/stripe/stripe";
import moment from "moment";
import SquarePaymentForm from "./SquarePaymentForm";
import { getSquareDetails } from "api/square/square";
import { getCompanyInfoFun } from "api/company/company";
import PrivacyPolicy from "./PrivacyPolicy";
import RentalAgreement from "./RentalAgreement";
import Cancellation from "./Cancellation";

export interface CheckOutPagePageMainProps {
  className?: string;
}

const CheckOutPagePageMain: FC<CheckOutPagePageMainProps> = ({
  className = "",
}) => {
  const {
    user,
    startDate,
    endDate,
    pickupLocation,
    dropOffLocation,
    setCouponResponse,
    checkCouponResponseLoading,
    selectedCar,
    searchedPickupLoction,
    searchedDropoffLocation,
    bookingdetails,
    callCouponAPI,
    saveCard,
    couponResponse,
    dropoffLocationType,
    callremoveCouponAPI,
    ourCompany,
    companyDetails,
  } = useData();
  const [inputValue, setInputValue] = useState("");
  let [isAgreementOpen, setIsAgreementOpen] = useState(false);
  let [isTermOpen, setIsTermOpen] = useState(false);
  let [isCancellationOpen, setIsCancellationOpen] = useState(false);
  let [rentalAgreementChecked, setRentalAgreementChecked] = useState(false);
  let [loading, setLoading] = useState(false);
  let [privacyPolicyCheckedChecked, setPrivacyPolicyChecked] = useState(false);
  let [agreementContent, setAgreementContent] = useState<any>([]);
  const [privacyPolicy, setPrivacyPolicy] = useState<any>([]);
  const [cancellationPolicy, setCancellationPolicy] = useState<any>([]);
  const [squareDetails, setSquareDetails] = useState<any>();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isCustomAgreementOpen, setIsCustomAgreementOpen] = useState(false);

  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP") {
      navigate("/", { replace: true });
    }
  }, [navigationType, navigate]);

  const handlePaymentSuccess = (data: any) => {
    toast.success("Payment completed successfully!");
    navigate(`/booking-details?id=${bookingdetails?.unique_identifier}`);
  };

  const handlePaymentError = (error: any) => {
    toast.error(error);
  };

  const cardElementOptions: StripeCardElementOptions = {
    style: {
      base: {
        color: `${companyDetails?.text_color}`,
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: `${companyDetails?.text_color}`,
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  useEffect(() => {
    const openRentalAgreement = async () => {
      if (bookingdetails && globalJson && bookingdetails?.customer?.email) {
        const encodedData = encodeBase64(bookingdetails?.customer?.email); // Encoding data to UTF-8
        const path = `${bookingdetails.id}b6cr4${ourCompany?.company}b6cr4${encodedData}`;

        try {
          const response = await getRentalAgreementContent(path);
          if (response?.data) {
            setAgreementContent(response.data);
            setPrivacyPolicy(response.data.content.privacy_policy);
            setCancellationPolicy(response.data.content.cancellation_policy);
          }
        } catch (error) {
          console.error("Error fetching rental agreement content:", error);
        }

        // setUrl(`https://dev.1now.app/pdf?agreement=${path}`);
      }
    };

    openRentalAgreement();
  }, [bookingdetails, globalJson, user]); // Add dependencies to avoid unnecessary re-renders

  useEffect(() => {
    callFetchSquareDetails();
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!pickupLocation) {
      navigate("/");
    }
  }, [pickupLocation]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleAgreementOpen = async (isOpen: boolean) => {
    const data = await getCompanyInfoFun();
    if (data?.default_agreement === "custom") {
      const encodedData = encodeBase64(bookingdetails?.customer?.email);
      const path = `${bookingdetails?.id}b6cr4${ourCompany?.company}b6cr4${encodedData}`;

      let url = "";
      if (process.env.REACT_APP_ENV === "dev") {
        url = "https://dev.1now.app/pdf?agreement=";
      } else if (process.env.REACT_APP_ENV === "stage") {
        url = "https://stage.1now.app/pdf?agreement=";
      } else {
        url = "https://1now.app/pdf?agreement=";
      }

      const iframeUrl = `${url}${path}`;
      setAgreementContent({ ...agreementContent, iframeUrl });
      setIsCustomAgreementOpen(isOpen); // open the custom modal
    } else {
      setIsAgreementOpen(isOpen);
      // if (isOpen) {
      //   toast(
      //     "Please review the Rental Agreement carefully before proceeding.",
      //     {
      //       style: {
      //         background: "#fff3cd",
      //         color: "#856404",
      //       },
      //     }
      //   );
      // }
    }
  };

  const handleCouponSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    callCouponAPI(inputValue, bookingdetails?.id);
    setInputValue("");
  };

  useEffect(() => {
    if (inputValue === "") {
      setCouponResponse(null);
    }
  }, [inputValue]);

  // Handle payment submission
  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (rentalAgreementChecked !== true) {
      toast.error("Please agree to the Rental Agreement");
      return;
    }
    if (privacyPolicyCheckedChecked !== true) {
      toast.error("Please agree to Privacy & Cancellation Policies");
      return;
    }
    if (!stripe || !elements) return;

    setLoading(true);

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      toast.error("Card element not found.");
      setLoading(false);
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement,
    });
    if (error) {
      toast.error("Payment failed: " + error.message);
      setLoading(false);
      return;
    } else {
      payDone(paymentMethod.id);
    }
  };

  const payDone = async (paymentMethodId: any) => {
    setLoading(true);
    try {
      await saveCard(paymentMethodId);
    } catch (error) {
      console.error("Error saving card", error);
    }
    try {
      const response = await PaymentDone(paymentMethodId, bookingdetails.id);
      if (response?.error) {
        toast.error(response?.error);
      } else {
        toast.success("Payment completed successfully!");
        navigate(`/booking-details?id=${bookingdetails?.unique_identifier}`);
      }
    } catch (error: any) {
      console.error("Payment Error", error);
    } finally {
      setLoading(false);
    }
  };
  const callFetchSquareDetails = async () => {
    setLoading(true);
    try {
      const resp = await getSquareDetails();
      setSquareDetails(resp);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderSidebar = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-md lg:border border-secondary-100 px-0 sm:p-6 xl:p-8 mt-4">
        <div className="flex flex-col sm:flex-row sm:items-center">
          <div className="flex-shrink-0 w-full"></div>
        </div>
        <div className="flex flex-col space-y-4">
          <div className="w-full mx-auto">
            <div className=" border-b border-secondary-100 pb-2">
              <h2 className="text-2xl font-bold text-text-color">Invoice</h2>
              <p className="mt-2 text-sm text-text-color">
                Prices may change depending on the length of the rental and the
                price of your rental car.
              </p>
            </div>
            <div className="mt-4">
              <div className="pb-4">
                <span className="text-lg font-semibold text-text-color">
                  Trip Price
                </span>
              </div>
              <div className="flex justify-between ">
                <span className="text-xl font-medium text-text-color truncate flex-1">
                  {bookingdetails?.fleet?.name}
                </span>
                <div className="flex flex-col items-end">
                  <span className="text-xl font-medium text-text-color">
                    ${Number(bookingdetails?.invoice.car_total).toFixed(2)}
                  </span>
                  <span className="text-sm text-text-color ">
                    $
                    {Number(
                      bookingdetails?.invoice?.avg_car_price_per_day
                    ).toFixed(2) + " x "}
                    {bookingdetails?.invoice.trip_days + "-Days  "}
                  </span>
                </div>
              </div>
            </div>

            {bookingdetails?.invoice?.location_total !== "0.00" && (
              <div className="mt-4">
                <div className="pb-4">
                  <span className="text-lg font-semibold text-text-color">
                    Location Total
                  </span>
                </div>
                {bookingdetails?.invoice?.breakdown?.locations?.map(
                  (location: any) => (
                    <>
                      <div className="flex justify-between ">
                        <span className="truncate flex-1 text-text-color">
                          {location.name}
                        </span>
                        <div className="flex flex-col items-end">
                          <span className="text-xl font-medium text-text-color">
                            ${Number(location.total).toFixed(2)}
                          </span>
                          <span className="text-sm text-text-color">
                            {location.rate}
                          </span>
                        </div>
                      </div>
                    </>
                  )
                )}
              </div>
            )}

            {bookingdetails?.invoice?.insurance_total !== null && (
              <div className="mt-4">
                <div className="pb-4">
                  <span className="text-lg font-semibold text-text-color">
                    Insurance Total
                  </span>
                </div>
                {bookingdetails?.invoice?.breakdown?.insurance?.map(
                  (insurance: any, index: any) => (
                    <div key={index} className="flex justify-between">
                      <span className="truncate flex-1  text-text-color">{insurance?.name}</span>
                      <div className="flex flex-col items-end">
                        <span className="text-xl font-medium text-text-color">
                          ${Number(insurance?.total).toFixed(2)}
                        </span>
                        <span className="text-sm text-text-color">
                          {insurance?.rate}
                        </span>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

            {bookingdetails?.invoice?.extras_total !== "0.00" && (
              <div className="mt-4">
                <div className="pb-4">
                  <span className="text-lg font-semibold text-text-color">
                    Trip Extras
                  </span>
                </div>
                {bookingdetails?.invoice?.breakdown?.extras?.map(
                  (extra: any) => (
                    <>
                      <div className="flex justify-between ">
                        <span className="truncate flex-1 text-text-color">{extra.name}</span>
                        <div className="flex flex-col items-end">
                          <span className="text-xl font-medium text-text-color">
                            ${Number(extra.total).toFixed(2)}
                          </span>
                          <span className="text-text-color">${extra.rate}</span>
                        </div>
                      </div>
                    </>
                  )
                )}
              </div>
            )}
            {bookingdetails?.invoice?.booking_fee !== "0.00" && (
              <div className="mt-4">
                <div className="pb-4">
                  <span className="text-lg font-semibold text-text-color">
                    Booking Fee
                  </span>
                </div>
                {bookingdetails?.invoice?.breakdown?.booking_fee?.map(
                  (bookingFee: any) => (
                    <>
                      <div className="flex justify-between ">
                        <span className="truncate flex-1 text-text-color">
                          {bookingFee.description
                            ? bookingFee.description
                            : bookingFee.name}
                        </span>
                        <div className="flex flex-col items-end">
                          <span className="text-xl font-medium text-text-color">
                            ${Number(bookingFee.total).toFixed(2)}
                          </span>
                          <span className="text-text-color">
                            {bookingFee.rate.includes("%")
                              ? bookingFee.rate
                              : `$${bookingFee.rate}`}
                          </span>
                        </div>
                      </div>
                    </>
                  )
                )}
              </div>
            )}

            {bookingdetails?.invoice?.tax !== "0.00" && (
              <div className="mt-4">
                <div className="pb-4">
                  <span className="text-lg font-semibold text-text-color">
                    Tax
                  </span>
                </div>
                {bookingdetails?.invoice?.breakdown?.taxes?.map(
                  (taxes: any) => (
                    <>
                      <div className="flex justify-between ">
                        <span className="truncate flex-1 text-text-color">
                          {taxes.description ? taxes.description : taxes.name}
                        </span>
                        <div className="flex flex-col items-end">
                          <span className="text-xl font-medium text-text-color">
                            ${Number(taxes.total).toFixed(2)}
                          </span>
                          <span className="text-text-color">
                            {taxes.rate.includes("%")
                              ? taxes.rate
                              : `$${taxes.rate}`}
                          </span>
                        </div>
                      </div>
                    </>
                  )
                )}
              </div>
            )}

            {bookingdetails?.invoice?.discount !== "0.00" && (
              <div className="mt-4">
                <div className="flex justify-between ">
                  <span className="text-lg font-normal text-text-color">
                    Discount
                  </span>
                  <span className="text-xl font-medium text-text-color">
                    - ${Number(bookingdetails?.invoice?.discount).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
            {couponResponse !== null && (
              <div className="mt-4 ">
                <div className="flex justify-between ">
                  <div className="flex flex-wrap justify-between border border-secondary-100 rounded-md min-w-[50%] p-[0.25rem] items-center">
                    <span
                      className="text-lg font-normal text-text-color pl-4 w-28 truncate "
                      title={couponResponse?.coupon_info?.name}
                    >
                      {couponResponse?.coupon_info?.name}
                    </span>
                    <span className="text-lg text-green-600  font-medium pl-1">
                      (
                      {couponResponse?.coupon_info?.coupon_type === "amount"
                        ? `$${couponResponse?.coupon_discount} off`
                        : `${couponResponse?.coupon_percentage}% off`}
                      )
                    </span>
                    <ButtonCircle
                      onClick={() => {
                        setCouponResponse(null);
                        callremoveCouponAPI(
                          couponResponse?.coupon_info?.name,
                          bookingdetails?.id
                        );
                      }}
                      className="self-center"
                    >
                      <XMarkIcon className="w-4 h-4 text-button-color" />
                    </ButtonCircle>
                  </div>
                  <span className="text-2xl text-green-600 flex flex-nowrap items-center font-semibold">
                    -${couponResponse?.coupon_discount}
                  </span>
                </div>
              </div>
            )}

            {couponResponse === null && (
              <form
                onSubmit={handleCouponSubmit}
                className="mt-10 relative w-full"
              >
                <Input
                  value={inputValue}
                  onChange={handleInputChange}
                  required
                  aria-required
                  placeholder="Promo Code"
                  type="text"
                  rounded="rounded-md w-full pr-0"
                />
                <button
                  type="submit"
                  className="absolute transform top-1/2 -translate-y-1/2 right-[5px] cursor-pointer ttnc-ButtonCircle flex items-center justify-center rounded-md !leading-none disabled:bg-opacity-70 bg-primary-6000 hover:bg-primary-700 text-button-color px-8 py-3"
                >
                  {checkCouponResponseLoading ? (
                    <div className="flex justify-center items-center col-span-full">
                      <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-secondary-100"></div>
                    </div>
                  ) : (
                    "Apply"
                  )}
                </button>
              </form>
            )}
            <div className="mt-4 border-secondary-100 pt-4">
              <div className="flex justify-between ">
                <div>
                  <span className="text-lg font-semibold text-text-color">
                    Total Rental Price
                  </span>
                  <p className="mt-1 text-sm text-text-color ">
                    Overall price rental
                  </p>
                </div>
                <div>
                  <span className="text-xl font-medium text-text-color">
                    $
                    {Number(
                      bookingdetails?.final_price_without_security
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {bookingdetails?.final_security_deposit !== 0 && (
              <div className="mt-4">
                <div className="flex justify-between ">
                  <span className="text-lg font-semibold text-text-color">
                    Refundable Deposit
                  </span>
                  <span className="text-xl font-medium text-text-color">
                    ${Number(bookingdetails?.final_security_deposit).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {!ourCompany?.default_payment ? (
              <div className="mt-8 pt-8 border-t border-secondary-100">
                <div className="text-yellow-600 dark:text-yellow-500 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="font-semibold">Payment system unavailable</p>
                  <p className="text-sm mt-1">
                    Our payment system is temporarily unavailable. Please try
                    again later or contact support.
                  </p>
                </div>
              </div>
            ) : ourCompany?.default_payment === "square" ? (
              squareDetails?.application_id ? (
                <SquarePaymentForm
                  squareDetails={squareDetails}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  amount={bookingdetails?.final_price_without_security}
                  bookingId={bookingdetails?.id}
                  renterId={bookingdetails?.customer?.id || ""}
                  rentalAgreementChecked={rentalAgreementChecked}
                  privacyPolicyCheckedChecked={privacyPolicyCheckedChecked}
                />
              ) : null
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mt-8 pt-8">
                  <span className="text-lg text-text-color font-semibold">
                    Payment Information
                  </span>
                  <div className="p-3 mt-4 border border-secondary-100 rounded-md">
                    <CardNumberElement
                      id="card-number-element"
                      options={cardElementOptions}
                      className="w-full"
                    />
                  </div>

                  {/* Expiry and CVC on a new row */}
                  <div className="flex mt-4 space-x-4">
                    {/* Expiry Date */}
                    <div className="flex-1 border border-secondary-100 p-3 rounded-md">
                      <CardExpiryElement
                        id="card-expiry-element"
                        options={cardElementOptions}
                        className="w-full"
                      />
                    </div>

                    {/* CVC */}
                    <div className="flex-1 border border-secondary-100 p-3 rounded-md">
                      <CardCvcElement
                        id="card-cvc-element"
                        options={cardElementOptions}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
                <ButtonPrimary
                  className="w-full mt-4 rounded-md"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex justify-center items-center col-span-full">
                      <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-secondary-100"></div>
                    </div>
                  ) : (
                    "Pay and Book"
                  )}
                </ButtonPrimary>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  };

  const mainSecion = () => {
    return (
      <div className="flex flex-col sm:rounded-md sm:border border-secondary-100 space-y-8 px-0 sm:p-6 xl:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className=" w-full aspect-w-4 aspect-h-2 rounded-md overflow-hidden">
            <img
              alt=""
              className="absolute inset-0 object-cover w-full"
              sizes="200px"
              src={
                (bookingdetails?.fleet?.fleet_photos?.length ?? 0) > 0
                  ? `https://fleet-management-images-upload-be.s3.amazonaws.com/${bookingdetails?.fleet?.fleet_photos[0].photo_url}`
                  : "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
              }
            />
          </div>
          <div>
            <div className="mt-4 max-w-xs">
              <p className="font-bold text-lg text-text-color">Pick up Details</p>
              <p className="text-text-color">
                {" "}
                {searchedPickupLoction
                  ? searchedPickupLoction
                  : pickupLocation?.name}
              </p>
              <div className="flex justify-between text-text-color">
                <span>{moment(startDate).format("ddd, D MMM YYYY")}</span>
                <span>{moment(startDate).format("hh:mm A")}</span>
              </div>
            </div>
            <div className="mt-4 max-w-xs">
              <p className="font-bold text-lg text-text-color">Drop off Details</p>
              <p className="text-text-color">
                {" "}
                {dropoffLocationType === false &&
                searchedDropoffLocation.length > 0
                  ? searchedDropoffLocation
                  : dropOffLocation?.name
                  ? dropOffLocation?.name
                  : dropoffLocationType === true &&
                    searchedPickupLoction.length > 0
                  ? searchedPickupLoction
                  : pickupLocation?.name}
              </p>
              <div className="flex justify-between text-text-color">
                <span>{moment(endDate).format("ddd, D MMM YYYY")}</span>
                <span>{moment(endDate).format("hh:mm A")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const termsSection = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-md sm:border border-secondary-100  space-y-8 px-0 sm:p-6 xl:p-8 mt-4">
        <div>
          <h3 className="text-2xl font-bold text-text-color ">
            Terms & Conditions
          </h3>

          <div className="flex gap-2 mt-6 p-4 border border-secondary-100  rounded-md overflow-hidden z-10">
            <Checkbox
              checked={rentalAgreementChecked}
              onChange={(checked) => {
                handleAgreementOpen(true);
              }}
              className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset  mr-4"
            >
              <CheckIcon
                className={`size-5 stroke-primary-200 ${
                  rentalAgreementChecked ? "visible" : "invisible"
                }`}
              />
            </Checkbox>
            <div>
              <div className="text-text-color text-lg font-medium ">
                I have read and accept all the terms and conditions mentioned
                below and i verify that all the details mentioned below are
                correct
              </div>
              <div
                className="text-text-color text-xl font-bold cursor-pointer underline"
                onClick={() => {
                  handleAgreementOpen(true);
                }}
              >
                Rental Agreement{" "}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-6 p-4 border border-secondary-100  rounded-md overflow-hidden z-10">
            <Checkbox
              checked={privacyPolicyCheckedChecked}
              onChange={(checked) => {
                setPrivacyPolicyChecked(checked);
              }}
              className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset  mr-4"
            >
              <CheckIcon
                className={`size-5 stroke-primary-200 ${
                  privacyPolicyCheckedChecked ? "visible" : "invisible"
                }`}
              />
            </Checkbox>
            <div>
              <div className="text-text-color text-lg font-medium ">
                I have read and accept all the policy and cancellation policy
                mentioned in the rental agreement and i verify that all the
                details mentioned in the agreement are correct
              </div>

              <span
                className="text-text-color  text-xl font-bold cursor-pointer underline"
                onClick={() => {
                  setIsTermOpen(true);
                }}
              >
                Privacy Policy
              </span>
              <span className="text-text-color  text-xl font-bold cursor-pointer mx-2">
                &
              </span>
              <span
                className="text-text-color  text-xl font-bold cursor-pointer underline"
                onClick={() => {
                  setIsCancellationOpen(true);
                }}
              >
                Cancellation Policy
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`nc-CheckOutPagePageMain ${className}`}>
        <main className="container mt-11 pb-10">
          <div className="max-w-2xl mx-auto">
            <div className="mt-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="focus:outline-none"
                aria-label="Go back"
              >
                <svg
                  width="45"
                  height="45"
                  viewBox="0 0 45 45"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.5 41.25C32.8553 41.25 41.25 32.8553 41.25 22.5C41.25 12.1446 32.8553 3.74998 22.5 3.74998C12.1447 3.74998 3.75 12.1446 3.75 22.5C3.75 32.8553 12.1447 41.25 22.5 41.25Z"
                    stroke="currentColor"
                    strokeWidth="2.8125"
                    className="text-text-color"
                  />
                  <path
                    d="M25.3125 30C25.3125 30 19.6875 24.4762 19.6875 22.5C19.6875 20.5235 25.3125 15 25.3125 15"
                    stroke="currentColor"
                    strokeWidth="2.8125"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-text-color"
                  />
                </svg>
              </button>
            </div>
            {mainSecion()}
            {termsSection()}
            {renderSidebar()}
          </div>
        </main>
      </div>

      <Transition appear show={isTermOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => {
            setIsTermOpen(false);
          }}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isTermOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4">
                      <button className="focus:outline-none focus:ring-0">
                        <XMarkIcon
                          onClick={() => {
                            setIsTermOpen(false);
                          }}
                          className="w-5 h-5 text-text-color "
                        />
                      </button>
                    </div>

                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-bg-color z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          <PrivacyPolicy terms={privacyPolicy} />
                        </div>
                      </div>
                    </div>

                    <div className=" justify-end px-4 py-3 bg-bg-color border-t border-secondary-100  flex">
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-md"
                        onClick={() => {
                          setPrivacyPolicyChecked(true);
                          setIsTermOpen(false);
                        }}
                      >
                        Accept
                      </ButtonPrimary>
                    </div>
                  </>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={isAgreementOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => {
            setIsAgreementOpen(false);
          }}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isAgreementOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4">
                      <button className="focus:outline-none focus:ring-0">
                        <XMarkIcon
                          onClick={() => {
                            setIsAgreementOpen(false);
                          }}
                          className="w-5 h-5 text-text-color "
                        />
                      </button>
                    </div>

                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-bg-color z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          <RentalAgreement data={agreementContent} />
                        </div>
                      </div>
                    </div>
                    <div className=" justify-end px-4 py-3 bg-bg-color border-t border-secondary-100  flex">
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-md"
                        onClick={() => {
                          setRentalAgreementChecked(true);
                          setIsAgreementOpen(false);
                        }}
                      >
                        Accept
                      </ButtonPrimary>
                    </div>
                  </>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={isCancellationOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => {
            setIsCancellationOpen(false);
          }}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isCancellationOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4 z-0">
                      <button className="focus:outline-none focus:ring-0">
                        <XMarkIcon
                          onClick={() => {
                            setIsCancellationOpen(false);
                          }}
                          className="w-5 h-5 text-text-color "
                        />
                      </button>
                    </div>

                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-bg-color z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          <Cancellation terms={cancellationPolicy} />
                        </div>
                      </div>
                    </div>
                    <div className=" justify-end px-4 py-3 bg-bg-color border-t border-secondary-100  flex">
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-md"
                        onClick={() => {
                          setPrivacyPolicyChecked(true);
                          setIsCancellationOpen(false);
                        }}
                      >
                        Accept
                      </ButtonPrimary>
                    </div>
                  </>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={isCustomAgreementOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsCustomAgreementOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isCustomAgreementOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4">
                      <button
                        className="focus:outline-none focus:ring-0"
                        onClick={() => setIsCustomAgreementOpen(false)}
                      >
                        <XMarkIcon className="w-5 h-5 text-text-color " />
                      </button>
                    </div>
                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-bg-color z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          {agreementContent?.iframeUrl && (
                            <iframe
                              src={agreementContent.iframeUrl}
                              title="Rental Agreement"
                              style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                                minHeight: 600,
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="justify-end px-4 py-3 bg-bg-color border-t border-secondary-100 flex">
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-md"
                        onClick={() => {
                          setRentalAgreementChecked(true);
                          setIsCustomAgreementOpen(false);
                        }}
                      >
                        Accept
                      </ButtonPrimary>
                    </div>
                  </>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CheckOutPagePageMain;
